import { Link } from 'react-router-dom';
import { getMenuItems } from '../modules/menu';
import { usePublicConfig, useFeatureEnabled } from '../hooks/usePublicConfig';
import { env } from '../config';
import { BrandLockup } from './BrandLockup';
import { BrandWatermark } from './site/BrandWatermark';
import { NewsletterForm } from './NewsletterForm';
import { SocialLinks } from './SocialLinks';

export const Footer = () => {
  const { data: publicConfig } = usePublicConfig();
  const newsletterOn = useFeatureEnabled('newsletter');
  const menuItems = getMenuItems(publicConfig?.modules, publicConfig?.menu);
  const siteName = publicConfig?.site?.name || env.VITE_SITE_NAME; // runtime site.name, env fallback

  return (
    <footer className="relative overflow-hidden isolate bg-primary-dark text-inverse-muted pt-20 pb-10">
      {/* Brand layers (PRP-15): forest glow, lime top hairline, the logo as a watermark */}
      <div className="absolute inset-0 -z-10 bg-[radial-gradient(ellipse_at_top_left,rgb(var(--color-primary-rgb)/0.6),transparent_60%)]" aria-hidden="true" />
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-accent/60 to-transparent" aria-hidden="true" />
      <BrandWatermark
        variant="mark"
        tone="white"
        corner="br"
        rotate={-10}
        size="w-[38rem] sm:w-[52rem] lg:w-[60rem]"
        opacityClass="opacity-[0.06]"
      />
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16" data-reveal-stagger>

          {/* Brand column */}
          <div className="space-y-6">
            {/* Full-color logo on a light radial plate (the official colors, not the white mark). */}
            <Link to="/" className="logo-plate focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent" aria-label="freshSTART CDC home">
              <BrandLockup size="lg" />
            </Link>
            <p className="text-sm leading-relaxed text-inverse-muted">
              Changing People · Moving Communities · Transforming Our World
            </p>
            {/* Social profiles from VITE_SOCIAL_* (hidden until set — PRD OQ 8) */}
            <SocialLinks />
          </div>

          {/* Explore links */}
          <div>
            <h4 className="text-inverse font-bold mb-6">Explore</h4>
            <ul className="space-y-4 text-sm">
              {menuItems.map(item => {
                const to = item.path.startsWith('/') ? item.path : `/${item.path}`;
                return (
                  <li key={item.path}>
                    {item.external ? (
                      <a
                        href={item.path}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group inline-flex items-center gap-1.5 hover:text-accent transition-colors"
                      >
                        {item.title}
                        <svg className="btn-arrow w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M13 6l6 6-6 6" /></svg>
                      </a>
                    ) : (
                      <Link to={to} className="group inline-flex items-center gap-1.5 hover:text-accent transition-colors">
                        {item.title}
                        <svg className="btn-arrow w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M13 6l6 6-6 6" /></svg>
                      </Link>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Newsletter - spans 2 cols on lg; runtime-gated (features.newsletter) */}
          {newsletterOn && (
          <div className="lg:col-span-2">
            <h4 className="text-inverse font-bold mb-6">Stay in the loop</h4>
            <p className="text-sm text-inverse-muted mb-6">
              Impact stories and ways to help, straight to your inbox.
            </p>
            <NewsletterForm variant="footer" />
          </div>
          )}

        </div>

        {/* Bottom bar: client copyright + KG ProDesign stamp */}
        <div className="pt-8 border-t border-surface-dark flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-inverse-muted" style={{ fontFamily: 'var(--font-mono)' }}>
            &copy; {new Date().getFullYear()} {siteName} ·{' '}
            <Link to="/privacy" className="hover:text-accent transition-colors underline-offset-2 hover:underline">
              Privacy
            </Link>
          </p>
          {/* Developed by KG ProDesign (brand: white logo variant on dark footers) */}
          <a
            href="https://kgprodesign.com"
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-center gap-3"
          >
            <img
              src="/kgprodesign-wht.webp"
              alt=""
              width={240}
              height={75}
              loading="lazy"
              decoding="async"
              className="h-6 w-auto opacity-80 group-hover:opacity-100 transition-opacity"
            />
            <span className="text-sm text-inverse-muted group-hover:text-white transition-colors">
              Developed by <span className="font-bold text-white">KG ProDesign</span>
              <span className="sr-only"> (opens in a new tab)</span>
            </span>
          </a>
        </div>
      </div>
    </footer>
  );
};
